"use strict";
/*******************************************************************************

    Contains definition for the BOA Client of TypeScript.
    It is responsible for requesting and receiving responses from
    the BOA API server(Stoa).
    It also provides other functions.(Verification of signature and pre-image)

    Copyright:
        Copyright (c) 2020-2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BOAClient = void 0;
var Hash_1 = require("../common/Hash");
var Transaction_1 = require("../data/Transaction");
var TxPayloadFee_1 = require("../utils/TxPayloadFee");
var Utils_1 = require("../utils/Utils");
var ErrorTypes_1 = require("./error/ErrorTypes");
var Request_1 = require("./Request");
var Balance_1 = require("./response/Balance");
var TransactionFee_1 = require("./response/TransactionFee");
var UnspentTxOutput_1 = require("./response/UnspentTxOutput");
var Validator_1 = require("./response/Validator");
var jsbi_1 = __importDefault(require("jsbi"));
var urijs_1 = __importDefault(require("urijs"));
/**
 * Define the BOA Client of TypeScript.
 * It is responsible for requesting and receiving responses from
 * the BOA API server(Stoa).
 * It also provides other functions. (Verification of signature and pre-image)
 */
var BOAClient = /** @class */ (function () {
    /**
     * Constructor
     * @param server_url The Stoa server URL
     * @param agora_url  The Agora server URL
     */
    function BOAClient(server_url, agora_url) {
        this.server_url = urijs_1.default(server_url);
        this.agora_url = urijs_1.default(agora_url);
    }
    /**
     * Request all valid validator at the block height.
     * If block height is not specified, it is the current height.
     * @param height The block height
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getAllValidators = function (height) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("validators");
            if (height !== undefined)
                url.addSearch("height", height);
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                var validators = new Array();
                if (response.status === 200) {
                    response.data.forEach(function (elem) {
                        var validator = new Validator_1.Validator();
                        validator.fromJSON(elem);
                        validators.push(validator);
                    });
                    resolve(validators);
                }
                else if (response.status === 204) {
                    resolve(validators);
                }
                else {
                    // It is not yet defined in Stoa.
                    reject(ErrorTypes_1.handleNetworkError({ response: response }));
                }
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Requests a valid validator for the address at the block height.
     * If block height is not specified, it is the current height.
     * @param address The public key
     * @param height  The block height
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getValidator = function (address, height) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("validator").filename(address);
            if (height !== undefined)
                url.addSearch("height", height);
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                var validators = new Array();
                if (response.status === 200) {
                    response.data.forEach(function (elem) {
                        var validator = new Validator_1.Validator();
                        validator.fromJSON(elem);
                        validators.push(validator);
                    });
                    resolve(validators);
                }
                else if (response.status === 204) {
                    resolve(validators);
                }
                else {
                    // It is not yet defined in Stoa.
                    reject(ErrorTypes_1.handleNetworkError({ response: response }));
                }
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Check the validity of a new pre-image
     * @param original_image        The original pre-image hash
     * @param original_image_height The original pre-image height
     * @param new_image             The new pre-image hash to check
     * @param new_image_height      The new pre-image height
     * @returns
     * {result: true, message: "The pre-image is valid."} if the pre-image is valid,
     * otherwise the result is false and the message is the reason for invalid
     * See_Also: https://github.com/bosagora/agora/blob/93c31daa616e76011deee68a8645e1b86624ce3d/source/agora/consensus/validation/PreImage.d#L50-L69
     */
    BOAClient.prototype.isValidPreimage = function (original_image, original_image_height, new_image, new_image_height) {
        if (!Number.isInteger(original_image_height) || original_image_height < 0)
            return {
                result: false,
                message: "The original pre-image height is not valid.",
            };
        if (!Number.isInteger(new_image_height) || new_image_height < 0)
            return {
                result: false,
                message: "The new pre-image height is not valid.",
            };
        if (new_image_height < original_image_height)
            return {
                result: false,
                message: "The height of new pre-image is smaller than that of original one.",
            };
        var temp_hash = new Hash_1.Hash(new_image.data);
        for (var idx = original_image_height; idx < new_image_height; idx++)
            temp_hash = Hash_1.hash(temp_hash.data);
        if (!original_image.data.equals(temp_hash.data))
            return {
                result: false,
                message: "The pre-image has a invalid hash value.",
            };
        return {
            result: true,
            message: "The pre-image is valid.",
        };
    };
    /**
     * Request the block height corresponding to to the block creation time
     * @param when Unix epoch time
     * @returns height If it already exists in the block,
     * it returns the height of the block,
     * if the block has not yet been created,
     * it returns the estimated height is returned.
     */
    BOAClient.prototype.getHeightAt = function (when) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var time = Math.ceil(when.getTime() / 1000);
            var url = urijs_1.default(_this.server_url).directory("block_height_at").filename(time.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                if (response.status === 200)
                    resolve(Number(response.data));
                else
                    reject(new Error("The date before Genesis Block creation is invalid."));
                reject(ErrorTypes_1.handleNetworkError({ response: response }));
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request the creation time of the entered block height.
     * @param height The block height
     * @returns Returns the creation time if a block of the entered height exists.
     * Otherwise, returns the expected creation time.
     */
    BOAClient.prototype.getHeightToTime = function (height) {
        var _this = this;
        return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                this.getBlockHeight()
                    .then(function (last) {
                    var url = urijs_1.default(_this.server_url).directory("/wallet/blocks/header");
                    if (jsbi_1.default.lessThanOrEqual(height, last))
                        url.addSearch("height", height.toString());
                    Request_1.Request()
                        .get(url.toString())
                        .then(function (response) {
                        var stoa_height = jsbi_1.default.BigInt(response.data.height);
                        var stoa_timestamp = Number(response.data.time_stamp);
                        var estimated_timestamp = stoa_timestamp +
                            jsbi_1.default.toNumber(jsbi_1.default.multiply(jsbi_1.default.subtract(height, stoa_height), jsbi_1.default.BigInt(60 * 10)));
                        resolve(estimated_timestamp);
                    })
                        .catch(function (reason) {
                        reject(ErrorTypes_1.handleNetworkError(reason));
                    });
                })
                    .catch(reject);
                return [2 /*return*/];
            });
        }); });
    };
    /**
     * Saves the data to the blockchain
     * @param tx The instance of the Transaction
     * @returns Returns true if success, otherwise returns false
     */
    BOAClient.prototype.sendTransaction = function (tx) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.agora_url).filename("transaction");
            Request_1.Request()
                .post(url.toString(), { tx: tx })
                .then(function (response) {
                if (response.status === 200)
                    resolve(true);
                else
                    reject(ErrorTypes_1.handleNetworkError({ response: response }));
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request UTXOs of public key
     * @param address The address of UTXOs
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getUTXOs = function (address) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("utxo").filename(address.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                var utxos = new Array();
                response.data.forEach(function (elem) {
                    utxos.push(UnspentTxOutput_1.UnspentTxOutput.reviver("", elem));
                });
                resolve(utxos);
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request UTXO's information about the UTXO hash array.
     * @param hashes The hash of UTXOs
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getUTXOInfo = function (hashes) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("utxos");
            var utxo_hashes = hashes.map(function (m) { return m.toString(); });
            Request_1.Request()
                .post(url.toString(), { utxos: utxo_hashes })
                .then(function (response) {
                var utxos = new Array();
                response.data.forEach(function (elem) {
                    utxos.push(UnspentTxOutput_1.UnspentTxOutput.reviver("", elem));
                });
                resolve(utxos);
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request an Stoa's current block height.
     */
    BOAClient.prototype.getBlockHeight = function () {
        var url = urijs_1.default(this.server_url).filename("/block_height");
        return Request_1.Request()
            .get(url.toString())
            .then(function (response) {
            return jsbi_1.default.BigInt(response.data);
        });
    };
    /**
     * Returns the connection status to Stoa.
     */
    BOAClient.prototype.getStoaStatus = function () {
        var url = urijs_1.default(this.server_url).filename("/block_height");
        return Request_1.Request()
            .get(url.toString())
            .then(function (response) {
            return Utils_1.Utils.isInteger(response.data);
        })
            .catch(function (reason) {
            return false;
        });
    };
    /**
     * Returns the connection status to Agora.
     */
    BOAClient.prototype.getAgoraStatus = function () {
        var url = urijs_1.default(this.agora_url).filename("/block_height");
        return Request_1.Request()
            .get(url.toString())
            .then(function (response) {
            return Utils_1.Utils.isInteger(response.data);
        })
            .catch(function (reason) {
            return false;
        });
    };
    /**
     * Request an appropriate fee based on the size of the transaction to Stoa.
     * @param tx_size The size of the transaction
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getTransactionFee = function (tx_size) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("transaction/fees").filename(tx_size.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                if (response.status === 200) {
                    resolve(TransactionFee_1.TransactionFee.reviver("", response.data));
                }
                else {
                    // It is not yet defined in Stoa.
                    reject(ErrorTypes_1.handleNetworkError({ response: response }));
                }
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request a history of transactions.
     * @param address   An address that want to be inquired.
     * @param page_size Maximum record count that can be obtained from one query
     * @param page      The number on the page, this value begins with 1
     * @param type      The parameter `type` is the type of transaction to query. ("payment", "freeze")
     * @param begin     The start date of the range of dates to look up.
     * @param end       The end date of the range of dates to look up.
     * @param peer      This is used when users want to look up only specific
     * Peer is the withdrawal address in the inbound transaction and a deposit address
     * in the outbound transaction address of their counterparts.
     * @deprecated Use getWalletTransactionHistory
     */
    BOAClient.prototype.getWalletTransactionsHistory = function (address, page_size, page, type, begin, end, peer) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url)
                .directory("/wallet/transactions/history/")
                .filename(address.toString())
                .addSearch("pageSize", page_size.toString())
                .addSearch("page", page.toString())
                .addSearch("type", type.join(","));
            if (begin !== undefined)
                url.addSearch("beginDate", begin.toString());
            if (end !== undefined)
                url.addSearch("endDate", end.toString());
            if (peer !== undefined)
                url.addSearch("peer", peer);
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                resolve(response.data);
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request a history of transactions.
     * @param address   An address that want to be inquired.
     * @param page_size Maximum record count that can be obtained from one query
     * @param page      The number on the page, this value begins with 1
     * @param type      The parameter `type` is the type of transaction to query. ("inbound", "outbound")
     * @param begin     The start date of the range of dates to look up.
     * @param end       The end date of the range of dates to look up.
     * @param peer      This is used when users want to look up only specific
     * Peer is the withdrawal address in the inbound transaction and a deposit address
     * in the outbound transaction address of their counterparts.
     */
    BOAClient.prototype.getWalletTransactionHistory = function (address, page_size, page, type, begin, end, peer) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url)
                .directory("/wallet/transaction/history/")
                .filename(address.toString())
                .addSearch("pageSize", page_size.toString())
                .addSearch("page", page.toString())
                .addSearch("type", type.join(","));
            if (begin !== undefined)
                url.addSearch("beginDate", begin.toString());
            if (end !== undefined)
                url.addSearch("endDate", end.toString());
            if (peer !== undefined)
                url.addSearch("peer", peer);
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                resolve(response.data);
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request a overview of a transaction.
     * @param tx_hash The hash of the transaction
     * @deprecated Use getWalletTransactionDetail
     */
    BOAClient.prototype.getWalletTransactionOverview = function (tx_hash) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("/wallet/transaction/overview").filename(tx_hash.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                if (response.status === 200) {
                    resolve(response.data);
                }
                else {
                    // It is not yet defined in Stoa.
                    reject(ErrorTypes_1.handleNetworkError({ response: response }));
                }
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request a detail of a transaction.
     * @param tx_hash The hash of the transaction
     */
    BOAClient.prototype.getWalletTransactionDetail = function (tx_hash) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("/wallet/transaction/detail").filename(tx_hash.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                if (response.status === 200) {
                    resolve(response.data);
                }
                else {
                    // It is not yet defined in Stoa.
                    reject(ErrorTypes_1.handleNetworkError({ response: response }));
                }
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request pending transactions.
     * @param address The input address of the pending transaction
     */
    BOAClient.prototype.getWalletTransactionsPending = function (address) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("/wallet/transactions/pending").filename(address.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                resolve(response.data);
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request a pending transaction based on the hash of the transaction.
     * @param tx_hash The hash of the transaction
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getPendingTransaction = function (tx_hash) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("transaction/pending").filename(tx_hash.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                if (response.status === 200) {
                    var tx = Transaction_1.Transaction.reviver("", response.data);
                    resolve(tx);
                }
                else {
                    // It is not yet defined in Stoa.
                    reject(ErrorTypes_1.handleNetworkError({ response: response }));
                }
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request a transaction based on the hash of the transaction.
     * @param tx_hash The hash of the transaction
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getTransaction = function (tx_hash) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("transaction").filename(tx_hash.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                if (response.status === 200) {
                    var tx = Transaction_1.Transaction.reviver("", response.data);
                    resolve(tx);
                }
                else {
                    // It is not yet defined in Stoa.
                    reject(ErrorTypes_1.handleNetworkError({ response: response }));
                }
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request a fee for a voting transaction
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getVotingFee = function (payload_size) {
        var _this = this;
        return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
            var payload_fee, tx_size, fees, tx_fee, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        payload_fee = TxPayloadFee_1.TxPayloadFee.getFee(payload_size);
                        tx_size = Transaction_1.Transaction.getEstimatedNumberOfBytes(1, 2, payload_size);
                        return [4 /*yield*/, this.getTransactionFee(tx_size)];
                    case 1:
                        fees = _a.sent();
                        tx_fee = jsbi_1.default.BigInt(fees.high);
                        resolve(jsbi_1.default.add(payload_fee, tx_fee));
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _a.sent();
                        reject(error_1);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); });
    };
    /**
     * Request UTXOs of public key
     * @param address The address of UTXOs
     * @param amount  The amount required
     * @param type    The type of balance (0: spendable, 1: frozen, 2: locked)
     * @param last    The last UTXO hash of previous inquiries.
     * The first time request, this value is undefined.
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getWalletUTXOs = function (address, amount, type, last) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url)
                .directory("wallet/utxo")
                .filename(address.toString())
                .addSearch("amount", amount.toString())
                .addSearch("type", type);
            if (last !== undefined)
                url.addSearch("last", last.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                var utxos = new Array();
                response.data.forEach(function (elem) {
                    utxos.push(UnspentTxOutput_1.UnspentTxOutput.reviver("", elem));
                });
                resolve(utxos);
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Request a balance based on the address.
     * @param address The address
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.getBalance = function (address) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("wallet/balance").filename(address.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                resolve(Balance_1.Balance.reviver("", response.data));
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    /**
     * Simple verify payment
     * Check if the transaction is stored in a block.
     * And check if Merkle's proof is valid.
     * @param tx_hash The hash of the transaction
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    BOAClient.prototype.verifyPayment = function (tx_hash) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var url = urijs_1.default(_this.server_url).directory("spv").filename(tx_hash.toString());
            Request_1.Request()
                .get(url.toString())
                .then(function (response) {
                resolve(response.data);
            })
                .catch(function (reason) {
                reject(ErrorTypes_1.handleNetworkError(reason));
            });
        });
    };
    return BOAClient;
}());
exports.BOAClient = BOAClient;
//# sourceMappingURL=BOAClient.js.map